'use client'

import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'
import AppLink from '@/components/elementary/AppLink'
import { MobileMenuToggle } from '@/components/elementary/navbar/mobile/MobileMenuToggle'
import { CaretRight } from '@/components/icons/CaretRight'
import { Paragraph } from '@/components/typography/Paragraph'
import { Button } from '@/components/ui/button'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { useIsMobile } from '@/hooks/useMobile'
import { usePathname } from '@/i18n/navigation'
import { MOBILE_MENU_ANIMATON_DURATION } from '@/lib/constants'
import { cn } from '@/lib/styles'
import { MOBILE_CATEGORY_NAVIGATION_TEST_IDS } from '@/lib/test-ids'
import type { GridCategoryNavigationData } from '@/types/general'
import { isValueEqualToPathname } from '@/utils/links'
import styles from '../../elementary/navbar/mobile/MobileMenu.module.css'

export function MobileCategoryNavigation({
	topCategories,
	restCategories,
	triggerLabel,
}: GridCategoryNavigationData & { triggerLabel: string }) {
	const t = useTranslations('categoryFilter')
	const pathname = usePathname()
	const categoryListRef = useRef<HTMLUListElement>(null)
	const isMobile = useIsMobile()

	const [isCategoriesMenuOpen, setCategoriesMenuOpen] = useState(false)
	const categories = [...topCategories, ...restCategories]

	// Lock body scroll when category menu is open
	useBodyScrollLock(isCategoriesMenuOpen && isMobile, categoryListRef.current ?? undefined)

	const scrollToTop = () => {
		categoryListRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
	}

	const handleMenuToggle = () => {
		if (isCategoriesMenuOpen) {
			// Scroll to top after state change, wait for menu close animation to finish
			setTimeout(() => scrollToTop(), MOBILE_MENU_ANIMATON_DURATION)
		}

		setCategoriesMenuOpen(!isCategoriesMenuOpen)
	}

	return (
		<div
			className="flex flex-col lg:hidden"
			data-test-id={MOBILE_CATEGORY_NAVIGATION_TEST_IDS.COMPONENT}
		>
			<Button
				onClick={() => setCategoriesMenuOpen(true)}
				variant="primaryLink"
				className="flex items-center justify-between py-6 border-y border-neutral-light-500"
				size="none"
				data-test-id={MOBILE_CATEGORY_NAVIGATION_TEST_IDS.OPEN}
			>
				<Paragraph fontSize="bodyRegularLarge">{triggerLabel}</Paragraph>
				<CaretRight />
			</Button>

			<div
				className={cn(
					'fixed z-51 top-0 left-0 w-full flex flex-col bg-neutral-light-100',
					'transition-transform duration-500 ease-in-out',
					isCategoriesMenuOpen ? 'translate-x-0 visible' : 'translate-x-full',
				)}
			>
				<div className="flex w-full justify-between items-center border-b border-neutral-light-500 px-6">
					<Paragraph
						fontSize="displaySmall"
						className={cn('w-full text-left py-4', styles['menu-link'])}
					>
						{t('categories')}
					</Paragraph>
					<MobileMenuToggle onClick={handleMenuToggle} isMobileMenuOpen={isCategoriesMenuOpen} />
				</div>

				<nav>
					<ul
						ref={categoryListRef}
						className="divide-y divide-neutral-light-500  overflow-y-scroll max-h-screen-minus-16 min-h-screen-minus-16 px-6"
					>
						{categories.map((category) => (
							<li key={category.documentId}>
								<AppLink
									href={category.link}
									scroll={false}
									className={cn(
										'py-8',
										isValueEqualToPathname(category.fullPath, pathname) && 'font-bold',
									)}
									data-test-id={MOBILE_CATEGORY_NAVIGATION_TEST_IDS.CATEGORY_ITEM}
									onClick={handleMenuToggle}
								>
									<Paragraph
										fontSize="displaySmall"
										className={cn('text-inherit', styles['menu-link'])}
									>
										{category.title}
									</Paragraph>
								</AppLink>
							</li>
						))}
					</ul>
				</nav>
			</div>
		</div>
	)
}
